import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Send, Sparkles, Square, Wand2, X } from "lucide-react";
import type { UIMessage } from "ai";
import { AnswerCard } from "@/components/AnswerCard";
import { ComposerAttach } from "@/components/Attachments";
import { ChatMessage } from "@/components/ChatMessage";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { cardOf, mockupsOf, openingCard, type Move } from "@/lib/cards";
import { useBriefActions, type BriefDetail, type Section } from "@/lib/briefs";
import { TECHNIQUES, useBriefChat, useChatHistory, type Technique } from "@/lib/chat";

/**
 * The interview (SPEC §5): one thread per brief, one question at a time, one
 * place to answer. Chips where a tap beats typing, the six techniques behind
 * one button, FAST or DETAIL, and plain words when the AI is unavailable or
 * capped. Tells the editor which box the AI wrote, so the panel above follows.
 */
export function ChatPane({
  detail,
  focus,
  onClearFocus,
  onBriefWrite,
  onOpenSection,
}: {
  detail: BriefDetail;
  focus: Section | null;
  onClearFocus: () => void;
  onBriefWrite: (sectionIds: string[]) => void;
  /** A chip naming a box opens it, as picking it in the rail does. */
  onOpenSection?: (sectionId: string) => void;
}) {
  const { brief, access, sections } = detail;
  const actions = useBriefActions(brief.id);
  const canEdit = access.canEdit;
  const history = useChatHistory(brief.id);
  const chat = useBriefChat(brief.id, history.data, onBriefWrite);
  const [draft, setDraft] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const busy = chat.status === "submitted" || chat.status === "streaming";

  const rootRef = useRef<HTMLDivElement>(null);
  const nav = useChatNav(rootRef, listRef);
  // The newest message comes into view only when the person was already at the
  // end, so reading back is not interrupted; after they send, always.
  const follow = useRef(false);
  const opened = useRef(false);
  useEffect(() => {
    // The transcript arriving opens the chat at its latest message.
    if (!opened.current && chat.messages.length > 0) {
      opened.current = true;
      follow.current = true;
    }
    if (follow.current || nav.atBottomRef.current) nav.toLatest("auto");
    follow.current = false;
    nav.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.messages, chat.status]);

  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 44), 160)}px`;
  }, [draft]);

  // The techniques menu closes on a click elsewhere or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const aiEnabled = history.data?.ai.enabled ?? true;
  const allowance = history.data?.ai.allowance ?? null;
  const capped = allowance?.capped ?? false;
  const canChat = canEdit && aiEnabled && !capped;

  const latestMockupId = [...chat.messages].reverse().flatMap((m) => (m.role === "assistant" ? mockupsOf(m) : []))[0]?.toolCallId ?? null;
  // `focusId` overrides the box in focus for this one message (a failed write's
  // "Make it shorter" asks about the box that did not save).
  const send = (text: string, action: Technique | null = null, parts: UIMessage["parts"] = [], focusId?: string | null) => {
    if (!canChat || busy) return;
    const trimmed = text.trim();
    if (!trimmed && !action) return;
    const label = trimmed || (action ? (TECHNIQUES.find((t) => t.key === action)?.label ?? "") : "");
    follow.current = true;
    void chat.sendMessage(
      { parts: [{ type: "text", text: label }, ...parts] },
      { body: { focusSectionId: focusId !== undefined && focusId !== null ? focusId : (focus?.id ?? null), action } },
    );
    setDraft("");
    setMenuOpen(false);
  };

  // A file attached from the composer goes to the box in focus, or the whole
  // brief; once it is in, the person's message tells the AI. The latest `send`
  // is used, so a turn that started meanwhile skips the message silently.
  const sendRef = useRef(send);
  sendRef.current = send;
  const attach = (file: File, sectionId: string | null) => {
    actions.upload.mutate(
      { file, sectionId },
      {
        onSuccess: ({ attachment }) => {
          const box = sections.find((s) => s.id === sectionId);
          sendRef.current(`I've attached "${attachment.filename}"${box ? ` to ${box.title}` : ""}.`, null, [
            { type: "data-attachment", data: { attachmentId: attachment.id, filename: attachment.filename } } as UIMessage["parts"][number],
          ]);
        },
      },
    );
  };

  const last = chat.messages[chat.messages.length - 1];
  const card =
    chat.messages.length === 0 ? openingCard(history.data?.openingChips ?? []) : last && last.role === "assistant" && !busy ? cardOf(last) : null;

  return (
    <div ref={rootRef} className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-4 py-2 lg:px-6">
        <Sparkles size={15} className="text-primary shrink-0" />
        <span className="text-sm font-medium">The interview</span>
        {focus ? (
          <span className="bg-accent text-accent-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
            about {focus.title}
            <button type="button" onClick={onClearFocus} aria-label="Stop focusing on this box" className="hover:text-foreground rounded-full">
              <X size={12} />
            </button>
          </span>
        ) : null}
        {canEdit ? (
          <div className="bg-secondary ml-auto flex rounded-md p-0.5 text-[11px]" role="radiogroup" aria-label="Thinking level">
            {(["fast", "detail"] as const).map((tier) => (
              <button
                key={tier}
                type="button"
                role="radio"
                aria-checked={brief.aiTier === tier}
                onClick={() => brief.aiTier !== tier && actions.patchBrief.mutate({ aiTier: tier })}
                title={tier === "fast" ? "Quick answers" : "Slower, thinks harder"}
                className={cn("rounded px-2 py-0.5 uppercase tracking-wide", brief.aiTier === tier ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")}
              >
                {tier}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div ref={listRef} role="log" aria-label="The interview so far" onScroll={nav.update} className="flex-1 space-y-4 overflow-y-auto px-4 pb-4 lg:px-6">
        {chat.messages.length === 0 ? (
          <div className="bg-accent/60 text-accent-foreground rounded-lg p-4 text-sm leading-relaxed">
            <p className="font-medium">Let's write this together.</p>
            <p className="mt-1">
              I'll ask one question at a time and write your answers into the boxes. Start from whichever end you know: what you want to get at the end, or
              what you already have. "Not sure" is a fine answer. I won't suggest how to build it; that is worked out with you later.
            </p>
          </div>
        ) : null}
        {chat.messages.map((m, i) => (
          <ChatMessage
            key={m.id}
            message={m}
            sections={sections}
            streaming={busy && i === chat.messages.length - 1}
            latestMockupId={latestMockupId}
            onOpenSection={onOpenSection}
            canEdit={canEdit}
            busy={busy}
            onShorten={canChat ? (sectionId) => send("", "shorter", [], sectionId) : undefined}
            onReact={canChat && !busy ? (text, data) => send(text, null, [{ type: "data-mockup", data } as UIMessage["parts"][number]]) : undefined}
          />
        ))}
        {chat.error ? (
          <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
            {chat.error.message}{" "}
            <button type="button" className="underline" onClick={() => void chat.regenerate()}>
              Try again
            </button>
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      <div className="border-border bg-card/95 sticky bottom-0 z-10 border-t px-4 py-3 backdrop-blur lg:relative lg:px-6">
        {/* Ways around a long chat, just above the composer on a phone and on a desktop. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-full flex items-end justify-between gap-2 px-4 pb-2 lg:px-6">
          <span>
            {nav.showBrief ? (
              <Pill onClick={nav.toBrief} label="Back to the brief" icon={<ArrowUp size={13} />} text="Brief" />
            ) : nav.showTop ? (
              <Pill onClick={nav.toTop} label="Back to the first message" icon={<ArrowUp size={13} />} text="Top" />
            ) : null}
          </span>
          <span>
            {nav.showLatest && chat.messages.length > 0 ? (
              <Pill onClick={() => nav.toLatest("smooth")} label="Jump to the latest message" icon={<ArrowDown size={13} />} text="Latest" />
            ) : null}
          </span>
        </div>
        {!aiEnabled ? (
          <p className="text-muted-foreground mb-2 text-xs">The AI is unavailable right now. Your brief still saves; write straight into the box above.</p>
        ) : capped ? (
          <p className="text-muted-foreground mb-2 text-xs">You've used today's {allowance?.limit} AI replies. The box above still saves; the AI is back tomorrow.</p>
        ) : null}

        {card && canChat ? (
          <AnswerCard
            key={last?.id ?? "opening"}
            card={card}
            disabled={busy}
            onAnswer={(text, data) => send(text, null, [{ type: "data-answer", data } as UIMessage["parts"][number]])}
            onMove={(m: Move) => send("", m)}
          />
        ) : null}

        <form
          className="relative flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <div ref={menuRef} className="relative shrink-0">
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={!canChat || busy}
              aria-label="Ask the AI to…"
              aria-expanded={menuOpen}
              title="Ask the AI to…"
              onClick={() => setMenuOpen((v) => !v)}
              className="h-11 w-11 rounded-full"
            >
              <Wand2 size={16} />
            </Button>
            {menuOpen ? (
              <div role="menu" className="bg-card border-border absolute bottom-full left-0 z-20 mb-2 w-72 rounded-lg border p-1 shadow-xl">
                <div className="text-muted-foreground px-2.5 pt-1.5 pb-1 text-[11px] font-medium uppercase tracking-wide">Ask the AI to…</div>
                {TECHNIQUES.map((t) => (
                  <button key={t.key} type="button" role="menuitem" onClick={() => send("", t.key)} className="hover:bg-accent block w-full rounded-md px-2.5 py-1.5 text-left">
                    <span className="block text-sm">{t.label}</span>
                    <span className="text-muted-foreground block text-[11px]">{t.hint}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {canEdit ? <ComposerAttach briefId={brief.id} sectionId={focus?.id ?? null} disabled={actions.upload.isPending} onPick={attach} /> : null}
          <textarea
            ref={area}
            value={draft}
            disabled={!canChat}
            rows={1}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(draft);
              }
            }}
            placeholder={canChat ? (focus ? `Answer about ${focus.title}…` : "Type your answer, or tap a reply above") : "The chat is off"}
            aria-label="Message"
            className="border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-ring min-h-11 flex-1 resize-none rounded-3xl border px-4 py-2.5 text-sm leading-snug focus-visible:outline-2 disabled:opacity-60"
          />
          {busy ? (
            <Button type="button" variant="outline" size="icon" onClick={() => void chat.stop()} aria-label="Stop" className="h-11 w-11 rounded-full">
              <Square size={14} />
            </Button>
          ) : (
            <Button type="submit" size="icon" disabled={!canChat || !draft.trim()} aria-label="Send" className="h-11 w-11 rounded-full">
              <Send size={16} />
            </Button>
          )}
        </form>
        {actions.upload.isPending ? <p className="text-muted-foreground mt-1 text-[11px]">Attaching…</p> : null}
        {actions.upload.isError ? <p className="text-destructive mt-1 text-xs">{(actions.upload.error as Error).message}</p> : null}
        {allowance && !capped && allowance.used > allowance.limit * 0.8 ? (
          <p className="text-muted-foreground mt-1 text-[11px]">{allowance.limit - allowance.used} AI replies left today.</p>
        ) : null}
      </div>
    </div>
  );
}

function Pill({ onClick, label, icon, text }: { onClick: () => void; label: string; icon: React.ReactNode; text: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="bg-card text-foreground border-border hover:bg-accent focus-visible:outline-ring pointer-events-auto inline-flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-medium shadow-md focus-visible:outline-2"
    >
      {icon}
      {text}
    </button>
  );
}

/** How close to the end counts as "at the latest message", in pixels. */
const NEAR_BOTTOM = 80;

/**
 * Where the person is in the chat, for the pills and for auto-scroll. Two
 * scroll containers: on a desktop the message list scrolls inside the fixed
 * grid; on a phone the list grows and the window scrolls. The list is the
 * scroller whenever it actually overflows.
 */
function useChatNav(rootRef: React.RefObject<HTMLDivElement | null>, listRef: React.RefObject<HTMLDivElement | null>) {
  const [state, setState] = useState({ showLatest: false, showTop: false, showBrief: false });
  const atBottomRef = useRef(true);

  const listScrolls = () => {
    const el = listRef.current;
    return !!el && el.scrollHeight > el.clientHeight + 1;
  };

  const update = () => {
    const el = listRef.current;
    let atBottom: boolean;
    let showTop = false;
    let showBrief = false;
    if (el && listScrolls()) {
      atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM;
      showTop = el.scrollTop > 200;
    } else {
      const doc = document.documentElement;
      atBottom = doc.scrollHeight - window.scrollY - window.innerHeight <= NEAR_BOTTOM;
      // The box sits right above the chat: once the chat's top has passed the
      // top of the screen, the brief is out of view.
      showBrief = (rootRef.current?.getBoundingClientRect().top ?? 0) < 0;
    }
    atBottomRef.current = atBottom;
    setState((prev) =>
      prev.showLatest === !atBottom && prev.showTop === showTop && prev.showBrief === showBrief ? prev : { showLatest: !atBottom, showTop, showBrief },
    );
  };

  useEffect(() => {
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toLatest = (behavior: ScrollBehavior) => {
    const el = listRef.current;
    if (el && listScrolls()) {
      if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight, behavior });
      else el.scrollTop = el.scrollHeight;
    } else {
      // The chat is the last thing on the page and its composer sticks to the bottom.
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior });
    }
    atBottomRef.current = true;
  };
  const toTop = () => {
    const el = listRef.current;
    if (!el) return;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: 0, behavior: "smooth" });
    else el.scrollTop = 0;
  };
  const toBrief = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return { ...state, atBottomRef, update, toLatest, toTop, toBrief };
}
